import type { ReactElement } from 'react';

/**
 * Monochrome line icons for the sidebar, drawn on a 24×24 grid with
 * `currentColor` strokes so they follow the nav item's text color in both
 * themes. Each one is a one-line-diagram or drawing-sheet glyph the
 * electrical user already reads: a breaker, a transformer, a motor circle.
 */
const base = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const NAV_ICONS: Record<string, ReactElement> = {
  // One-line: source bus feeding two branches
  'project-designer': (
    <svg {...base}>
      <rect x="8" y="3" width="8" height="4" rx="1" />
      <path d="M12 7v4M6 11h12M6 11v3M18 11v3" />
      <rect x="3.5" y="14" width="5" height="6" rx="1" />
      <rect x="15.5" y="14" width="5" height="6" rx="1" />
    </svg>
  ),
  // Conductor cross-section
  ampacity: (
    <svg {...base}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.5" />
    </svg>
  ),
  // Conduit end view with three conductors
  conduit: (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="9" r="2.2" />
      <circle cx="9" cy="14" r="2.2" />
      <circle cx="15" cy="14" r="2.2" />
    </svg>
  ),
  // Voltage falling along a run
  'voltage-drop': (
    <svg {...base}>
      <path d="M4 4v16h16" />
      <path d="M7 8h4l3 7 3-3h3" />
    </svg>
  ),
  // Motor: circle M
  motor: (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 16V8l4 5 4-5v8" />
    </svg>
  ),
  // Breaker on a feeder
  feeder: (
    <svg {...base}>
      <path d="M12 2v5M12 17v5" />
      <path d="M12 7l6 6" />
      <path d="M9 7h6M9 17h6" />
    </svg>
  ),
  // Several motors on one feeder
  'multi-motor-feeder': (
    <svg {...base}>
      <path d="M12 2v5M5 7h14" />
      <path d="M6 7v3M12 7v3M18 7v3" />
      <circle cx="6" cy="15" r="3.5" />
      <circle cx="12" cy="15" r="3.5" />
      <circle cx="18" cy="15" r="3.5" />
    </svg>
  ),
  // Hazardous location: flame in a warning triangle
  'hazardous-locations': (
    <svg {...base}>
      <path d="M12 3L2.5 20h19L12 3z" />
      <path d="M12 9.5c1.6 1.4 2.4 2.7 2.4 4.1a2.4 2.4 0 0 1-4.8 0c0-.9.3-1.6.9-2.2.1.8.5 1.2 1 1.2.4-1.2.6-2.2.5-3.1z" />
    </svg>
  ),
  // Panelboard with breaker rows
  'panel-schedule': (
    <svg {...base}>
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M12 6v12" />
      <path d="M7 8h2.5M7 11h2.5M7 14h2.5M7 17h2.5M14.5 8H17M14.5 11H17M14.5 14H17M14.5 17H17" />
    </svg>
  ),
  // Transformer: two windings
  transformer: (
    <svg {...base}>
      <circle cx="12" cy="9" r="5" />
      <circle cx="12" cy="15" r="5" />
      <path d="M12 1v3M12 20v3" />
    </svg>
  ),
  // Generator: circle G
  generator: (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 9.5A4 4 0 1 0 16 12h-4" />
    </svg>
  ),
  // Load analysis: bars
  'load-analysis': (
    <svg {...base}>
      <path d="M4 20h16" />
      <path d="M7 20v-7M12 20V6M17 20v-10" />
    </svg>
  ),
  // PLC I/O: terminal block with wires
  'plc-io': (
    <svg {...base}>
      <rect x="3" y="7" width="18" height="10" rx="1.5" />
      <path d="M7 12h.01M12 12h.01M17 12h.01" strokeWidth="2.5" />
      <path d="M7 7V4M12 7V4M17 7V4M7 17v3M12 17v3M17 17v3" />
    </svg>
  ),
  // Admin: sliders
  admin: (
    <svg {...base}>
      <path d="M4 7h10M18 7h2M4 12h3M11 12h9M4 17h12M20 17h0" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="18" cy="17" r="2" />
    </svg>
  ),
};

export function NavIcon({ id }: { id: string }) {
  return NAV_ICONS[id] ?? (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}
